import { useState } from "react";
import { classNames } from "primereact/utils";
import { useCashdeskProductSearch } from "../hooks/useCashdeskProductSearch";
import { CashdeskProductGrid } from "./CashdeskProductGrid";
import { CashdeskCart } from "./CashdeskCart";
import { CashdeskPaymentModal } from "./CashdeskPaymentModal";
import { CashdeskSuccessModal } from "./CashdeskSuccessModal";
import { ICategorizedInventoryData } from "../hooks/useCashdeskPage";
import { IStudioSettingsModel } from "../../../common/models/IStudioSettingsModel";
import { Sidebar } from "primereact/sidebar";
import { useAppContext } from "../../../AppContext";
import styles from "./CashdeskSaleTab.module.scss";

interface ICashdeskSaleTabProps {
  sale: ReturnType<typeof import("../hooks/useCashdeskSale").useCashdeskSale>;
  categorizedData?: ICategorizedInventoryData;
  isProductsLoading: boolean;
  studioSettings?: IStudioSettingsModel;
}

export const CashdeskSaleTab = ({
  sale,
  categorizedData,
  isProductsLoading,
  studioSettings,
}: ICashdeskSaleTabProps) => {
  const { isMobile } = useAppContext();
  const productSearch = useCashdeskProductSearch({
    onAutoAdd: sale.addToCart,
  });
  const [isMobileCartOpen, setIsMobileCartOpen] = useState(false);

  const itemCount = sale.cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <>
      <div className={styles.root}>
        <div className={styles.root_left}>
          <CashdeskProductGrid
            categories={categorizedData?.categories ?? []}
            items={categorizedData?.items ?? []}
            isLoading={isProductsLoading}
            activeCategoryId={sale.activeCategoryId}
            setActiveCategoryId={sale.setActiveCategoryId}
            productSearch={productSearch}
            onProductClick={sale.addToCart}
            sellableVouchers={sale.sellableVouchers}
            onVoucherClick={sale.addVoucherToCart}
            cartVoucherIds={sale.cart
              .filter((item) => !!item.giftVoucherId)
              .map((item) => item.giftVoucherId!)}
          />
        </div>

        {/* Desktop/tablet: inline cart panel */}
        <div className={styles.root_right} {...(!isMobile ? { "data-onboarding": "cashdesk-cart" } : {})}>
          <CashdeskCart
            cart={sale.cart}
            updateQuantity={sale.updateQuantity}
            setQuantity={sale.setQuantity}
            selectedCustomer={sale.selectedCustomer}
            setSelectedCustomer={sale.setSelectedCustomer}
            customerSearchQuery={sale.customerSearchQuery}
            setCustomerSearchQuery={sale.setCustomerSearchQuery}
            customerSearchResults={sale.customerSearchResults}
            discountPercent={sale.discountPercent}
            setDiscountPercent={sale.setDiscountPercent}
            subTotal={sale.subTotal}
            discountAmount={sale.discountAmount}
            total={sale.total}
            onPay={sale.openPaymentModal}
          />
        </div>
      </div>

      {/* Mobile: sticky bottom bar */}
      <div
        className={classNames(styles.mobile_bar, { [styles.mobile_bar_pulse]: sale.cart.length > 0 })}
        onClick={() => setIsMobileCartOpen(true)}
        {...(isMobile ? { "data-onboarding": "cashdesk-cart" } : {})}
      >
        <div className={styles.mobile_bar_left}>
          {sale.cart.length > 0 && (
            <span className={styles.mobile_bar_badge}>{itemCount}</span>
          )}
          <span className={styles.mobile_bar_label}>
            🛒 {sale.cart.length === 0 ? "Количка" : "Виж количка"}
          </span>
        </div>
        <div className={styles.mobile_bar_right}>
          <span className={styles.mobile_bar_total}>
            {sale.total.toFixed(2)} €
          </span>
        </div>
      </div>

      {/* Mobile: cart sidebar (slides up from bottom) */}
      <Sidebar
        visible={isMobileCartOpen}
        position="bottom"
        onHide={() => setIsMobileCartOpen(false)}
        className={styles.mobile_cart_sidebar}
        header={
          <div className={styles.mobile_cart_header}>
            <span>Количка</span>
            {sale.cart.length > 0 && (
              <span className={styles.mobile_cart_header_count}>
                {itemCount} {itemCount === 1 ? "артикул" : "артикула"}
              </span>
            )}
          </div>
        }
      >
        <div className={styles.mobile_cart_body}>
          <CashdeskCart
            cart={sale.cart}
            updateQuantity={sale.updateQuantity}
            setQuantity={sale.setQuantity}
            selectedCustomer={sale.selectedCustomer}
            setSelectedCustomer={sale.setSelectedCustomer}
            customerSearchQuery={sale.customerSearchQuery}
            setCustomerSearchQuery={sale.setCustomerSearchQuery}
            customerSearchResults={sale.customerSearchResults}
            discountPercent={sale.discountPercent}
            setDiscountPercent={sale.setDiscountPercent}
            subTotal={sale.subTotal}
            discountAmount={sale.discountAmount}
            total={sale.total}
            onPay={() => {
              setIsMobileCartOpen(false);
              sale.openPaymentModal();
            }}
          />
        </div>
        <div className={styles.mobile_cart_pay_wrapper}>
          <button
            className={styles.mobile_cart_pay_btn}
            disabled={sale.cart.length === 0}
            onClick={() => {
              setIsMobileCartOpen(false);
              sale.openPaymentModal();
            }}
          >
            💳 Плати {sale.total > 0 ? `· ${sale.total.toFixed(2)} €` : ""}
          </button>
        </div>
      </Sidebar>

      <CashdeskPaymentModal
        visible={sale.isPaymentModalOpen && !sale.successOrder}
        total={sale.total}
        paymentOptions={sale.paymentOptions}
        selectedPaymentType={sale.selectedPaymentType}
        onSelectPaymentType={sale.setSelectedPaymentType}
        receivedAmount={sale.receivedAmount}
        onReceivedAmountChange={sale.setReceivedAmount}
        changeAmount={sale.changeAmount}
        paymentAmountState={sale.paymentAmountState}
        isPaymentMethodEnabled={sale.isPaymentMethodEnabled}
        notes={sale.notes}
        onNotesChange={sale.setNotes}
        onConfirm={sale.confirmPayment}
        onCancel={sale.closePaymentModal}
        isSubmitting={sale.isSubmitting}
        giftVoucherCode={sale.giftVoucherCode}
        onGiftVoucherCodeChange={sale.changeVoucherCode}
        onCheckGiftVoucher={sale.checkVoucherCode}
        payingVoucher={sale.payingVoucher}
        isLookingUpVoucher={sale.isLookingUpVoucher}
        isPayingWithVoucher={sale.isPayingWithVoucher}
        isVoucherTenderReady={sale.isVoucherTenderReady}
      />

      <CashdeskSuccessModal
        visible={!!sale.successOrder}
        order={sale.successOrder}
        onNewOrder={sale.startNewOrder}
      />
    </>
  );
};
